/**
 * 로마 화성 문자열 끝의 유니코드 피겨(위·아래첨자)를 파싱해
 * 악보/패널에서 수직 1열로 그릴 수 있는 구조로 분해한다.
 */

import { isChordSymbolIB64Figure } from './chordSymbols'

const SUP_TO_DIGIT = {
  '\u2070': '0',
  '\u00b9': '1',
  '\u00b2': '2',
  '\u00b3': '3',
  '\u2074': '4',
  '\u2075': '5',
  '\u2076': '6',
  '\u2077': '7',
  '\u2078': '8',
  '\u2079': '9',
}

const SUB_TO_DIGIT = {
  '\u2080': '0',
  '\u2081': '1',
  '\u2082': '2',
  '\u2083': '3',
  '\u2084': '4',
  '\u2085': '5',
  '\u2086': '6',
  '\u2087': '7',
  '\u2088': '8',
  '\u2089': '9',
}

function isSupDigit(ch) {
  return Object.prototype.hasOwnProperty.call(SUP_TO_DIGIT, ch)
}

function isSubDigit(ch) {
  return Object.prototype.hasOwnProperty.call(SUB_TO_DIGIT, ch)
}

function supToDigit(ch) {
  return SUP_TO_DIGIT[ch] ?? ''
}

function subToDigit(ch) {
  return SUB_TO_DIGIT[ch] ?? ''
}

/** 위첨자 숫자–♭/b–아래첨자 숫자: mode mixture 등에서 5 / ♭3 수직열 */
function isFlatBetweenFigures(ch) {
  return ch === '\u266d' || ch === 'b'
}

/**
 * 슬래시 없는 한 구간을 오른쪽 끝부터 피겨 쌍/단일 위첨자로 분해.
 * stack에 topFlat이 있으면 차용 등에서 ♭⁵₃처럼 5음 내림을 위줄 왼쪽에 표시.
 * @returns {Array<{ type: 'text', text: string } | { type: 'stack', top: string, bottom: string, topFlat?: string } | { type: 'stack3', top: string, mid: string, bottom: string } | { type: 'supDigit', digit: string }>}
 */
function parseEndStack(s) {
  if (!s) return []
  const cps = Array.from(s)
  const n = cps.length
  /** ♭⁵₃: 근음·5음이 함께 내림된 차용 삼화음 등 (⁵♭₃와 구분: n-3이 플랫) */
  if (
    n >= 3 &&
    isSubDigit(cps[n - 1]) &&
    isSupDigit(cps[n - 2]) &&
    isFlatBetweenFigures(cps[n - 3])
  ) {
    const base = cps.slice(0, n - 3).join('')
    const top = supToDigit(cps[n - 2])
    const bottom = subToDigit(cps[n - 1])
    if (!top || !bottom) return [{ type: 'text', text: s }]
    const flatTok = cps[n - 3] === 'b' ? '\u266d' : cps[n - 3]
    return [...parseEndStack(base), { type: 'stack', top, bottom, topFlat: flatTok }]
  }
  if (
    n >= 3 &&
    isSubDigit(cps[n - 1]) &&
    isSubDigit(cps[n - 2]) &&
    isSupDigit(cps[n - 3])
  ) {
    const base = cps.slice(0, n - 3).join('')
    const top = supToDigit(cps[n - 3])
    const mid = subToDigit(cps[n - 2])
    const bottom = subToDigit(cps[n - 1])
    if (!top || !mid || !bottom) return [{ type: 'text', text: s }]
    return [...parseEndStack(base), { type: 'stack3', top, mid, bottom }]
  }
  if (
    n >= 3 &&
    isSubDigit(cps[n - 1]) &&
    isFlatBetweenFigures(cps[n - 2]) &&
    isSupDigit(cps[n - 3])
  ) {
    const base = cps.slice(0, n - 3).join('')
    const top = supToDigit(cps[n - 3])
    const bottomDigit = subToDigit(cps[n - 1])
    if (!top || !bottomDigit) return [{ type: 'text', text: s }]
    const bottom = (cps[n - 2] === 'b' ? '\u266d' : cps[n - 2]) + bottomDigit
    return [...parseEndStack(base), { type: 'stack', top, bottom }]
  }
  if (n >= 2 && isSubDigit(cps[n - 1]) && isSupDigit(cps[n - 2])) {
    const base = cps.slice(0, n - 2).join('')
    const top = supToDigit(cps[n - 2])
    const bottom = subToDigit(cps[n - 1])
    if (!top || !bottom) return [{ type: 'text', text: s }]
    return [...parseEndStack(base), { type: 'stack', top, bottom }]
  }
  if (
    n >= 2 &&
    cps[n - 1] === '\u00b9' &&
    cps[n - 2] === '\u00b9'
  ) {
    const base = cps.slice(0, n - 2).join('')
    return [...parseEndStack(base), { type: 'supDigit', digit: '11' }]
  }
  if (n >= 1 && isSupDigit(cps[n - 1])) {
    const base = cps.slice(0, n - 1).join('')
    const digit = supToDigit(cps[n - 1])
    if (!digit) return [{ type: 'text', text: s }]
    return [...parseEndStack(base), { type: 'supDigit', digit }]
  }
  return [{ type: 'text', text: s }]
}

/**
 * @param {string} str
 * @returns {{ mode: 'ib64', raw: string } | { mode: 'parts', parts: ReturnType<typeof parseEndStack> } | { mode: 'lines', lines: ReturnType<typeof parseChordSymbolParts>[] }}
 */
export function parseChordSymbolParts(str) {
  let s = String(str)
  // 공통화음 전조 붙임줄 표기('^' 접두) — 실제 렌더링 시에는 표시하지 않는다
  // (ScoreRenderer가 이 표식을 보고 이전 화음 기호에서 곡선을 그린다).
  if (s.startsWith('^')) s = s.slice(1)
  // 공통화음 전조 등 두 줄 병기(예: "C: v♭\nF: ii") — 줄바꿈으로 구분된 각 줄을 재귀 파싱.
  if (s.includes('\n')) {
    const lines = s.split('\n').map((line) => parseChordSymbolParts(line))
    return { mode: 'lines', lines }
  }
  if (isChordSymbolIB64Figure(s)) return { mode: 'ib64', raw: s }
  const i = s.indexOf('/')
  if (i >= 0) {
    const parts = [...parseEndStack(s.slice(0, i)), { type: 'text', text: s.slice(i) }]
    return { mode: 'parts', parts }
  }
  return { mode: 'parts', parts: parseEndStack(s) }
}

/**
 * 피겨 stack의 bottom이 ♭+한 자리 숫자일 때: 위·아래 숫자만 같은 세로열, ♭은 아래줄 왼쪽.
 * @returns {null | { flat: string, digit: string }}
 */
export function splitFiguredStackFlatDigitBottom(bottom) {
  const s = String(bottom)
  if (s.length !== 2) return null
  const a = s[0]
  const b = s[1]
  if (!/^\d$/.test(b)) return null
  if (a === '\u266d' || a === '♭') return { flat: '\u266d', digit: b }
  if (a === 'b') return { flat: '\u266d', digit: b }
  return null
}
